import React, { Children, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLink } from 'react-aria';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A component for navigating between routes
 */
const Link = forwardRef(function Link(props, forwardedRef) {
  let {
    href,
    title,
    target,
    hiddenText,
    asButton,
    type,
    size,
    iconType,
    iconPosition,
    className,
    tabIndex,
    onClick,
    onKeyDown,
    children
  } = props;

  // Links should use a forwarded ref else use its own.
  // https://react.dev/reference/react/forwardRef
  const linkRef = useRef(null);
  const ref = forwardedRef || linkRef;

  const { linkProps } = useLink(props, ref);

  const { t } = useTranslation();

  const linkClasses = asButton
    ? classNames('button', className, {
        ['button--' + type]: type,
        ['button--' + size]: size,
        'button--icon': iconType,
        ['icon--' + iconType]: iconType,
        ['icon--' + iconPosition]: iconPosition
      })
    : className;

  const path = typeof href === 'object' ? href.pathname || window.location.href : href;
  const isAbsolute = path.indexOf('://') > 0 || path.indexOf('//') === 0;
  const isMailTo = path.indexOf('mailto:') >= 0;
  const isTel = path.indexOf('tel:') >= 0;
  const isExternal = (() => {
    try {
      const url = new URL(path);
      const host = url.host;

      return !host.includes('.gov.wales') && !host.includes('.llyw.cymru') && !host.includes('localhost:8081');
    } catch (error) {
      return false;
    }
  })();

  function generateTitle() {
    if (!title) {
      let strings = [];

      Children.forEach(children, (child) => {
        if (typeof child === 'string') {
          strings.push(child);
        }
      });

      if (strings.length > 0) {
        title = strings[0];
      }
    }

    return title ? `${isMailTo ? t('styleguide:email_link_to') : t('styleguide:link_to')} ${title}` : undefined;
  }

  // function handleOnClick(e) {
  //   ref?.current?.blur();
  //   console.log('blurred')

  //   if (typeof onClick === 'function') {
  //     onClick(e);
  //   }
  // }

  if (isAbsolute || isMailTo || isTel) {
    return (
      <a
        ref={ref}
        href={href}
        className={linkClasses}
        title={generateTitle()}
        target={target || (isExternal && !isMailTo && !isTel ? '_blank' : undefined)}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {children}
        {(isMailTo || (isExternal && isAbsolute) || hiddenText) && (
          <span className="visually-hidden">
            {isMailTo
              ? ' (' + t('styleguide:opens_email_client') + ')'
              : isExternal && isAbsolute
              ? ' (' + t('styleguide:external_website') + ')'
              : hiddenText}
          </span>
        )}
      </a>
    );
  }

  return (
    <a
      {...linkProps}
      ref={ref}
      className={linkClasses}
      title={generateTitle()}
      target={target || (isExternal && !isMailTo && !isTel ? '_blank' : undefined)}
      tabIndex={tabIndex}
      // Remove focus styles affecting clicking and dragging for #13173.
      // https://dev.azure.com/ccdg/CWO%20-%20Events/_workitems/edit/13173
      onPointerDown={(e) => {
        linkProps?.onPointerDown(e);
        ref?.current?.blur();
      }}
    >
      {children}
      {(isMailTo || (isExternal && isAbsolute) || hiddenText) && (
        <span className="visually-hidden">
          {isMailTo
            ? t('styleguide:opens_email_client')
            : isExternal && isAbsolute
            ? t('styleguide:external_website')
            : hiddenText}
        </span>
      )}
    </a>
  );
});

Link.propTypes = {
  /**
   * The destination of the link
   */
  href: PropTypes.string.isRequired,
  /**
   * The title of the link
   */
  title: PropTypes.string,
  /**
   * The target of the link
   */
  target: PropTypes.string,
  /**
   * The hidden text for screenreaders
   */
  hiddenText: PropTypes.string,
  /**
   * The button flag
   */
  asButton: PropTypes.bool,
  /**
   * The type
   */
  type: PropTypes.string,
  /**
   * The size
   */
  size: PropTypes.string,
  /**
   * The icon type
   */
  iconType: PropTypes.string,
  /**
   * The icon position
   */
  iconPosition: PropTypes.string,
  /**
   * The classes
   */
  className: PropTypes.string,
  /**
   * The tab index
   */
  tabIndex: PropTypes.string,
  /**
   * The click handler
   */
  onClick: PropTypes.func,
  /**
   * The nested children
   */
  children: PropTypes.node.isRequired
};

export default Link;
